/**
 * Helpers
 */
import EA from './helpers/init';
import UserAgent from './helpers/userAgent';
import { eaRest } from './helpers/ea';
import CookieConsent from '@poddroom/cookie-consent';
import '@poddroom/cookie-consent/cookie-consent.css';

/* body class */
UserAgent.setBodyClass();

/**
 * Methods
 */
import './methods/exampleMethod.js';
import './methods/swiper.js';
import './methods/lightgallery.js';
import './methods/swiper2.js';
import './methods/swiper3.js';
import './methods/imageLazyLoad.js';
import './methods/videoLazyLoad.js';
import './methods/mobileMenu.js';
import './methods/generalDropdownLogic.js';
import './methods/listPagination.js';
import './methods/playVideoLogic.js';
import './methods/wakeLock.js';
import './methods/productDetailSwiper.js';
import './methods/socialShare.js';
import './methods/popupController.js';
import './methods/copyUrl.js';

import { createApp } from 'vue';
import { defineAsyncComponent } from 'vue';

const app = createApp({
    mounted() {
        EA.loadMethods();
    }
});

/* Components */
app.component('ExampleComponent', defineAsyncComponent(() => import('../../components/ExampleComponent.vue')));
app.component('Newsletter', defineAsyncComponent(() => import('../../components/Newsletter.vue')));

/* Add Pinia store */
// import { createPinia } from 'pinia';
// const pinia = createPinia();
// app.use(pinia);

app.mount('#app');

const config = window.coolinarikaConfig || {};
const translations = config.translations || {};
const language = config.language || 'de';
const t = (key) => translations[key] || key;
const format = (key, replacements) => Object.entries(replacements).reduce(
    (value, [name, replacement]) => value.replaceAll(`{${name}}`, replacement),
    t(key)
);
const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    '\'': '&#039;'
})[character]);
const privacyPolicyUrl = String(config.privacyPolicyUrl || '');
const privacyPolicyLink = `<a href="${escapeHtml(privacyPolicyUrl)}">${escapeHtml(t('consent.privacyPolicy'))}</a>`;
const alwaysEnabled = `<span class="pm__badge">${escapeHtml(t('consent.necessary.alwaysEnabled'))}</span>`;
const serviceDetailsHeaders = {
    name: t('consent.headers.name'),
    purpose: t('consent.headers.purpose'),
    provider: t('consent.headers.provider'),
    dataTransfer: t('consent.headers.dataTransfer'),
    retention: t('consent.headers.retention'),
    revocation: t('consent.headers.revocation')
};

document.querySelectorAll('[data-service="youtube"]').forEach((container) => {
    new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeName === 'IFRAME') {
                    window.dataLayer.push({ event: 'video_unlocked' });
                }
            }
        }
    }).observe(container, { childList: true, subtree: true });
});

function syncYoutubeConsentCookie(services) {
    const name = 'im_youtube_v2';
    const accepted = Array.isArray(services?.video) && services.video.includes('youtube');
    if (accepted) {
        const expires = new Date(Date.now() + 182 * 864e5).toUTCString();
        document.cookie = `${name}=1; Expires=${expires}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
    } else {
        document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    }
}

const META_PIXEL_ID = '1595350035715495';
const TIKTOK_PIXEL_ID = 'DAGG3VRC77UC8FLJTN7G';

function loadMetaPixel() {
    if (window.fbq) return;
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
    document,'script','https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
}

function loadTikTokPixel() {
    if (window.ttq) return;
    !function (w, d, t) {
        w.TiktokAnalyticsObject = t;
        const ttq = w[t] = w[t] || [];
        ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
        ttq.setAndDefer = function (target, method) {
            target[method] = function () {
                target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
            };
        };
        for (let i = 0; i < ttq.methods.length; i++) {
            ttq.setAndDefer(ttq, ttq.methods[i]);
        }
        ttq.load = function (id) {
            const src = 'https://analytics.tiktok.com/i18n/pixel/events.js';
            ttq._i = ttq._i || {};
            ttq._i[id] = [];
            ttq._i[id]._u = src;
            ttq._t = ttq._t || {};
            ttq._t[id] = +new Date();
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.src = `${src}?sdkid=${id}&lib=${t}`;
            const first = document.getElementsByTagName('script')[0];
            first.parentNode.insertBefore(script, first);
        };
        ttq.load(TIKTOK_PIXEL_ID);
        ttq.page();
    }(window, document, 'ttq');
}

function updateMarketingPixels(services) {
    const marketing = Array.isArray(services?.marketing) ? services.marketing : [];
    if (marketing.includes('meta_pixel')) loadMetaPixel();
    if (marketing.includes('tiktok_pixel')) loadTikTokPixel();
}

CookieConsent.init(
    {
        consentLogEndpoint: `${eaRest}consent`,
        privacyPolicyLink
    },
    {
        revision: window.cookieConsentRevision,
        onConsent: ({ cookie }) => {
            window.updateGoogleConsent(cookie.services);
            syncYoutubeConsentCookie(cookie.services);
            updateMarketingPixels(cookie.services);
        },
        onChange: ({ cookie }) => {
            window.updateGoogleConsent(cookie.services);
            syncYoutubeConsentCookie(cookie.services);
            updateMarketingPixels(cookie.services);
        },
        categories: {
            analytics: {
                autoClear: {
                    reloadPage: true
                },
                services: {
                    google_tag_manager: {
                        label: t('consent.service.analytics'),
                        cookies: [{ name: /^_ga/ }]
                    }
                }
            },
            marketing: {
                autoClear: {
                    reloadPage: true
                },
                services: {
                    google_ads: {
                        label: t('consent.service.marketing'),
                        cookies: [{ name: /^_gcl_/ }]
                    },
                    meta_pixel: {
                        label: t('consent.service.metaPixel'),
                        cookies: [{ name: /^_fbp$|^fr$/ }]
                    },
                    tiktok_pixel: {
                        label: t('consent.service.tiktokPixel'),
                        cookies: [{ name: /^_ttp$|^tt_/ }]
                    }
                }
            },
            video: {
                autoClear: {
                    cookies: [{ name: /^im_youtube(?:_v[12])?$/ }],
                    reloadPage: true
                },
                services: {
                    youtube: {
                        label: t('consent.service.video'),
                        onAccept: () => CookieConsent.IframeManager.acceptService('youtube'),
                        onReject: () => CookieConsent.IframeManager.rejectService('youtube')
                    }
                }
            }
        },
        language: {
            default: language,
            autoDetect: 'document',
            translations: {
                [language]: {
                    consentModal: {
                        title: t('consent.modal.title'),
                        description: t('consent.modal.description'),
                        acceptAllBtn: t('consent.acceptAll'),
                        acceptNecessaryBtn: t('consent.acceptNecessary'),
                        showPreferencesBtn: t('consent.showPreferences'),
                        footer: privacyPolicyLink
                    },
                    preferencesModal: {
                        title: t('consent.preferences.title'),
                        acceptAllBtn: t('consent.acceptAll'),
                        acceptNecessaryBtn: t('consent.acceptNecessary'),
                        savePreferencesBtn: t('consent.preferences.save'),
                        closeIconLabel: t('consent.preferences.close'),
                        serviceCounterLabel: t('consent.preferences.serviceCounter'),
                        sections: [
                            {
                                title: t('consent.intro.title'),
                                description: format('consent.intro.description', { privacyPolicyLink })
                            },
                            {
                                title: format('consent.necessary.title', { alwaysEnabled }),
                                description: t('consent.necessary.description'),
                                linkedCategory: 'necessary',
                                cookieTable: {
                                    headers: serviceDetailsHeaders,
                                    body: [{
                                        name: 'cc_cookie',
                                        purpose: t('consent.necessary.cookie.purpose'),
                                        provider: t('consent.necessary.cookie.provider'),
                                        dataTransfer: t('consent.necessary.cookie.dataTransfer'),
                                        retention: t('consent.necessary.cookie.retention'),
                                        revocation: t('consent.necessary.cookie.revocation')
                                    }]
                                }
                            },
                            {
                                title: t('consent.analytics.title'),
                                description: t('consent.analytics.description'),
                                linkedCategory: 'analytics',
                                cookieTable: {
                                    headers: serviceDetailsHeaders,
                                    body: [{
                                        name: t('consent.analytics.cookie.name'),
                                        purpose: t('consent.analytics.cookie.purpose'),
                                        provider: t('consent.analytics.cookie.provider'),
                                        dataTransfer: t('consent.analytics.cookie.dataTransfer'),
                                        retention: t('consent.analytics.cookie.retention'),
                                        revocation: t('consent.analytics.cookie.revocation')
                                    }]
                                }
                            },
                            {
                                title: t('consent.marketing.title'),
                                description: t('consent.marketing.description'),
                                linkedCategory: 'marketing',
                                cookieTable: {
                                    headers: serviceDetailsHeaders,
                                    body: [
                                        {
                                            name: t('consent.marketing.cookie.name'),
                                            purpose: t('consent.marketing.cookie.purpose'),
                                            provider: t('consent.marketing.cookie.provider'),
                                            dataTransfer: t('consent.marketing.cookie.dataTransfer'),
                                            retention: t('consent.marketing.cookie.retention'),
                                            revocation: t('consent.marketing.cookie.revocation')
                                        },
                                        {
                                            name: t('consent.marketing.metaPixel.cookie.name'),
                                            purpose: t('consent.marketing.metaPixel.cookie.purpose'),
                                            provider: t('consent.marketing.metaPixel.cookie.provider'),
                                            dataTransfer: t('consent.marketing.metaPixel.cookie.dataTransfer'),
                                            retention: t('consent.marketing.metaPixel.cookie.retention'),
                                            revocation: t('consent.marketing.metaPixel.cookie.revocation')
                                        },
                                        {
                                            name: t('consent.marketing.tiktokPixel.cookie.name'),
                                            purpose: t('consent.marketing.tiktokPixel.cookie.purpose'),
                                            provider: t('consent.marketing.tiktokPixel.cookie.provider'),
                                            dataTransfer: t('consent.marketing.tiktokPixel.cookie.dataTransfer'),
                                            retention: t('consent.marketing.tiktokPixel.cookie.retention'),
                                            revocation: t('consent.marketing.tiktokPixel.cookie.revocation')
                                        }
                                    ]
                                }
                            },
                            {
                                title: t('consent.video.title'),
                                description: t('consent.video.description'),
                                linkedCategory: 'video',
                                cookieTable: {
                                    headers: serviceDetailsHeaders,
                                    body: [
                                        {
                                            name: 'im_youtube_v2',
                                            purpose: t('consent.video.localCookie.purpose'),
                                            provider: t('consent.video.localCookie.provider'),
                                            dataTransfer: t('consent.video.localCookie.dataTransfer'),
                                            retention: t('consent.video.localCookie.retention'),
                                            revocation: t('consent.video.localCookie.revocation')
                                        },
                                        {
                                            name: '__Secure-YNID',
                                            purpose: t('consent.video.ynid.purpose'),
                                            provider: t('consent.video.google.provider'),
                                            dataTransfer: t('consent.video.google.dataTransfer'),
                                            retention: t('consent.video.google.retention'),
                                            revocation: t('consent.video.google.revocation')
                                        },
                                        {
                                            name: 'YSC',
                                            purpose: t('consent.video.ysc.purpose'),
                                            provider: t('consent.video.google.provider'),
                                            dataTransfer: t('consent.video.google.dataTransfer'),
                                            retention: t('consent.video.google.sessionRetention'),
                                            revocation: t('consent.video.google.revocation')
                                        },
                                        {
                                            name: 'VISITOR_INFO1_LIVE',
                                            purpose: t('consent.video.visitorInfo.purpose'),
                                            provider: t('consent.video.google.provider'),
                                            dataTransfer: t('consent.video.google.dataTransfer'),
                                            retention: t('consent.video.google.retention'),
                                            revocation: t('consent.video.google.revocation')
                                        },
                                        {
                                            name: 'VISITOR_PRIVACY_METADATA',
                                            purpose: t('consent.video.privacyMetadata.purpose'),
                                            provider: t('consent.video.google.provider'),
                                            dataTransfer: t('consent.video.google.dataTransfer'),
                                            retention: t('consent.video.google.retention'),
                                            revocation: t('consent.video.google.revocation')
                                        },
                                        {
                                            name: '__Secure-ROLLOUT_TOKEN',
                                            purpose: t('consent.video.rolloutToken.purpose'),
                                            provider: t('consent.video.google.provider'),
                                            dataTransfer: t('consent.video.google.dataTransfer'),
                                            retention: t('consent.video.google.retention'),
                                            revocation: t('consent.video.google.revocation')
                                        }
                                    ]
                                }
                            }
                        ]
                    }
                }
            }
        }
    },
    {
        services: {
            youtube: {
                embedUrl: '{data-id}',
                cookie: {
                    name: 'im_youtube_v2'
                },
                languages: {
                    [language]: {
                        notice: t('consent.video.notice'),
                        loadAllBtn: t('consent.video.loadAll')
                    }
                }
            }
        }
    }
);
